import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './NotificationSettings.css';

const NotificationSettings: React.FC = () => {
  const { user, updateNotificationPreferences } = useAuth();
  const [preferences, setPreferences] = useState({
    email: true,
    sms: true,
    push: true,
    orderUpdates: true,
    promotions: false
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (user?.notificationPreferences) {
      setPreferences(user.notificationPreferences);
    }
  }, [user]);

  const handleToggle = (key: keyof typeof preferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updateNotificationPreferences(preferences);
    if (result.success) {
      setMessage('Preferences saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } else {
      setMessage('Failed to save preferences');
      setTimeout(() => setMessage(''), 3000);
    }
    setSaving(false);
  };

  return (
    <div className="notification-settings-container">
      <div className="notification-settings-card">
        <h2>Notification Preferences</h2>
        <p>Choose how you want to receive updates</p>
        
        {message && <div className="status-message">{message}</div>}
        
        <div className="settings-section">
          <h3>Channel Preferences</h3>
          
          <div className="setting-toggle">
            <div className="toggle-info">
              <strong>Email Notifications</strong>
              <p>Receive updates via email</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.email}
                onChange={() => handleToggle('email')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="setting-toggle">
            <div className="toggle-info">
              <strong>SMS Notifications</strong>
              <p>Receive text message updates (if phone number provided)</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.sms}
                onChange={() => handleToggle('sms')}
                disabled={!user?.phone}
              />
              <span className="toggle-slider"></span>
            </label>
            {!user?.phone && (
              <small className="disabled-hint">Add phone number to enable SMS</small>
            )}
          </div>
          
          <div className="setting-toggle">
            <div className="toggle-info">
              <strong>Push Notifications</strong>
              <p>Receive browser notifications</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.push}
                onChange={() => handleToggle('push')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        
        <div className="settings-section">
          <h3>Content Preferences</h3>
          
          <div className="setting-toggle">
            <div className="toggle-info">
              <strong>Order Updates</strong>
              <p>Order confirmations, shipping updates, and delivery notifications</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.orderUpdates}
                onChange={() => handleToggle('orderUpdates')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="setting-toggle">
            <div className="toggle-info">
              <strong>Promotions & Offers</strong>
              <p>Special deals, discounts, and promotional content</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.promotions}
                onChange={() => handleToggle('promotions')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        
        <button 
          onClick={handleSave} 
          disabled={saving}
          className="save-preferences-btn"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;